import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import DatePicker from "@/components/DatePicker";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, Loader2, Pencil, AlertTriangle } from "lucide-react";

const VAL = {
  positivo: ["Positivo", "border-emerald-300 bg-emerald-100 text-emerald-800"],
  da_migliorare: ["Da migliorare", "border-amber-300 bg-amber-100 text-amber-800"],
  critico: ["Critico", "border-red-300 bg-red-100 text-red-700"],
};

// Gestisce l'affiancamento sia sulle visite (visita_partner) sia sull'attività di affiancamento
// del Responsabile (affiancamento_agenzia). Mostra "Senza nota" finché il Responsabile non compila la nota.
export default function AffiancamentoButton({ visit, onDone }) {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "commercial_manager";
  const isVisit = visit?.type === "visita_partner" || visit?.type === "visita";
  const isAffAgenzia = visit?.type === "affiancamento_agenzia";
  const visitId = isAffAgenzia ? visit?.affiancamento_visit_id : visit?.id;
  const aff = visit?.affiancamento;
  const senzaNota = !!visit?.affiancamento_senza_nota;
  // affiancamento attivo (richiesto da agente o già valutato) su una visita
  const affAttivo = isAffAgenzia ? true : !!aff?.attivo;
  const valutazione = isAffAgenzia ? visit?.valutazione : aff?.valutazione;
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ data: "", valutazione: "positivo", note: "" });

  if (!isVisit && !isAffAgenzia) return null;
  // Su un'attività di affiancamento senza visita collegata non c'è nulla da completare
  if (isAffAgenzia && !visitId) return null;

  // Non-manager: mostra solo lo stato (badge), niente azioni
  if (!canManage) {
    if (senzaNota) return <Badge variant="outline" className="border-amber-400 bg-amber-100 text-amber-800 text-[10px]" data-testid={`aff-senza-nota-${visit.id}`}><AlertTriangle className="mr-1 h-3 w-3" />Senza nota</Badge>;
    if (affAttivo && valutazione) return <Badge variant="outline" className={`text-[10px] ${VAL[valutazione]?.[1] || ""}`} data-testid={`aff-badge-${visit.id}`}>Affiancamento: {VAL[valutazione]?.[0] || valutazione}</Badge>;
    return null;
  }

  const openDlg = () => {
    setF({ data: aff?.data || visit.date || "", valutazione: valutazione || "positivo", note: (isAffAgenzia ? visit.notes : aff?.note) || "" });
    setOpen(true);
  };

  const save = async () => {
    if (!(f.note || "").trim()) { toast.error("Inserisci la nota dell'affiancamento"); return; }
    setSaving(true);
    try {
      await api.post(`/activities/${visitId}/affiancamento`, { data: f.data || null, valutazione: f.valutazione, note: f.note });
      toast.success("Nota affiancamento salvata");
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <>
      {senzaNota ? (
        <Button variant="outline" size="sm" className="h-7 border-amber-400 bg-amber-100 px-2 text-[11px] text-amber-800 hover:bg-amber-200" onClick={openDlg} data-testid={`aff-senza-nota-btn-${visit.id}`}>
          <AlertTriangle className="mr-1.5 h-3.5 w-3.5" /> Senza nota — aggiungi
        </Button>
      ) : affAttivo && (valutazione || (isAffAgenzia ? visit.notes : aff?.note)) ? (
        <button type="button" onClick={openDlg} className="inline-flex items-center gap-1" data-testid={`aff-edit-${visit.id}`}>
          <Badge variant="outline" className={`text-[10px] ${VAL[valutazione]?.[1] || "border-emerald-300 bg-emerald-100 text-emerald-800"}`}>
            <Users className="mr-1 h-3 w-3" />Affiancamento{valutazione ? `: ${VAL[valutazione]?.[0] || valutazione}` : ""}
          </Badge>
          <Pencil className="h-3 w-3 text-muted-foreground" />
        </button>
      ) : affAttivo ? (
        <button type="button" onClick={openDlg} className="inline-flex items-center gap-1" data-testid={`aff-pending-${visit.id}`}>
          <Badge variant="outline" className="border-orange-300 bg-orange-100 text-orange-800 text-[10px]"><Users className="mr-1 h-3 w-3" />Affiancamento previsto</Badge>
          <Pencil className="h-3 w-3 text-muted-foreground" />
        </button>
      ) : isVisit ? (
        <Button variant="outline" size="sm" className="h-7 px-2 text-[11px]" onClick={openDlg} data-testid={`aff-activate-${visit.id}`}>
          <Users className="mr-1.5 h-3.5 w-3.5" /> Attiva affiancamento
        </Button>
      ) : null}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="affiancamento-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-primary" /> Affiancamento — nota del Responsabile</DialogTitle>
            <DialogDescription>Compila la nota sull'affiancamento svolto insieme all'agente. La visita non viene reinserita.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Data affiancamento</Label>
              <DatePicker value={f.data} onChange={(v) => setF((s) => ({ ...s, data: v }))} data-testid="aff-data" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Valutazione affiancamento</Label>
              <Select value={f.valutazione} onValueChange={(v) => setF((s) => ({ ...s, valutazione: v }))}>
                <SelectTrigger data-testid="aff-valutazione"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(VAL).map(([k, v]) => <SelectItem key={k} value={k}>{v[0]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Note / coaching del responsabile *</Label>
              <Textarea rows={4} value={f.note} onChange={(e) => setF((s) => ({ ...s, note: e.target.value }))} placeholder="Osservazioni, punti di forza, aree di miglioramento per l'agente…" data-testid="aff-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving} data-testid="aff-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva nota"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
